// ghostRoute.ts — P1.2b GHOST-ROUTE encoding + spoiler-aware overlay helpers.
//
// When a board is opened from a friend's "beat my score" challenge link, we overlay the friend's
// route as FAINT footprints so the player races the ghost. This module owns the *data*: it encodes a
// compact, URL-safe per-cell route into the deep link (share.ts → &ghost=<compact>), decodes it back
// on boot (registryKeys → BootParams.challengeGhost → GameScene), and maps each cell to a spoiler-safe
// marker the renderer paints. It is framework-free and DOM-free (only a type-only import of RouteCell),
// so it round-trips in a plain `tsx` smoke with no Phaser/Vite in scope.
//
// SPOILER CONTRACT (mirrors share.ts buildRouteGrid): we encode ONLY what the friend actually DID —
//   • ate a CORRECT cell   → GHOST_ATE_CORRECT
//   • ate a DECOY cell      → GHOST_ATE_DECOY
//   • THREADED an armed decoy (routed through, never eaten) → GHOST_THREADED
// A cell the friend never touched stays GHOST_NONE, so the overlay NEVER leaks the kind of an
// un-eaten cell (an untouched correct and an untouched decoy are indistinguishable — both NONE).

import type { RouteCell } from './share';

/** Per-cell ghost outcome. 0 = untouched/wall (no marker), the rest are the friend's actual action. */
export type GhostOutcome = 0 | 1 | 2 | 3;
export const GHOST_NONE = 0 as const;
export const GHOST_ATE_CORRECT = 1 as const;
export const GHOST_ATE_DECOY = 2 as const;
export const GHOST_THREADED = 3 as const;

/**
 * Hard upper bound on the cells we encode. The board is 5×6 (30) on phones, 6×6 (36) on tablet; 64
 * keeps the payload tiny AND keeps the 2-char base36 length prefix valid (max 36² − 1 = 1295 ≫ 64).
 */
export const MAX_GHOST_CELLS = 64;

// URL-safe base64url alphabet (A–Z a–z 0–9 - _): 64 symbols = exactly 6 bits = three 2-bit cells per
// character, so a full 36-cell board packs into a 2-char count prefix + 12 payload chars.
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Coerce any value into a valid 0..3 outcome (corrupt/undefined → NONE, never a false marker). */
function clampOutcome(v: GhostOutcome | undefined): GhostOutcome {
  return v === 1 || v === 2 || v === 3 ? v : GHOST_NONE;
}

/**
 * Classify ONE board cell to its spoiler-free ghost outcome. Ordering mirrors share.ts
 * classifyRouteCell: a human EAT (eatenBy === 0) wins first, then a threaded (armed-but-not-eaten)
 * decoy. The sharer is always player 0, so an AI rival's eats are never miscredited to the ghost.
 */
export function ghostOutcomeForCell(cell: RouteCell): GhostOutcome {
  if (cell.kind === 'wall') return GHOST_NONE;
  if (cell.eaten && cell.eatenBy === 0) {
    return cell.kind === 'correct' ? GHOST_ATE_CORRECT : GHOST_ATE_DECOY;
  }
  // Only decoys ever arm; a threaded decoy was routed THROUGH (free Gauntlet pass), not eaten.
  if (cell.armed && cell.kind === 'decoy') return GHOST_THREADED;
  return GHOST_NONE;
}

/** Map a snapshot's final cells (in cell-id order) to the per-cell ghost outcomes the sharer earned. */
export function ghostOutcomesFromCells(cells: RouteCell[] | undefined): GhostOutcome[] {
  if (!cells || cells.length === 0) return [];
  return cells.slice(0, MAX_GHOST_CELLS).map(ghostOutcomeForCell);
}

/** True iff the route has at least one real footprint (so we never bloat the URL with an all-NONE run). */
export function hasGhostRoute(outcomes: GhostOutcome[]): boolean {
  for (const o of outcomes) if (o !== GHOST_NONE) return true;
  return false;
}

/**
 * Encode the per-cell outcomes to a compact, URL-safe string: a 2-char base36 cell-count prefix
 * followed by base64url payload (three 2-bit cells per char). Returns '' when the route is empty or
 * has no real footprints, so the caller can simply skip the &ghost param.
 */
export function encodeGhost(outcomes: GhostOutcome[]): string {
  const n = Math.min(outcomes.length, MAX_GHOST_CELLS);
  if (n <= 0 || !hasGhostRoute(outcomes.slice(0, n))) return '';
  const count = n.toString(36).padStart(2, '0');
  let packed = '';
  for (let i = 0; i < n; i += 3) {
    const c0 = clampOutcome(outcomes[i]);
    const c1 = clampOutcome(outcomes[i + 1]);
    const c2 = clampOutcome(outcomes[i + 2]);
    packed += ALPHABET[c0 | (c1 << 2) | (c2 << 4)]; // 0..63 → one base64url char
  }
  return count + packed;
}

/**
 * Decode a ghost string back to a per-cell outcome array (indexed by cell id). Corrupt characters
 * decode to NONE rather than throwing, so a mangled link degrades to "no ghost" instead of a crash or
 * a false marker. The result is exactly `count` long (padded with NONE if the payload was truncated).
 */
export function decodeGhost(str: string | null | undefined): GhostOutcome[] {
  if (!str || str.length < 3) return []; // need a 2-char count + at least one payload char
  const count = parseInt(str.slice(0, 2), 36);
  if (!Number.isFinite(count) || count <= 0 || count > MAX_GHOST_CELLS) return [];
  const payload = str.slice(2);
  const out: GhostOutcome[] = [];
  for (let i = 0; i < payload.length && out.length < count; i += 1) {
    const value = ALPHABET.indexOf(payload[i]);
    const v = value < 0 ? 0 : value; // unknown char → all-NONE triple (safe)
    out.push((v & 3) as GhostOutcome);
    out.push(((v >> 2) & 3) as GhostOutcome);
    out.push(((v >> 4) & 3) as GhostOutcome);
  }
  while (out.length < count) out.push(GHOST_NONE);
  out.length = count;
  return out;
}

/** Spoiler-free colour role for a ghost marker (maps to the same palette as the share legend). */
export type GhostColorRole = 'correct' | 'decoy' | 'threaded';

/** Presentation descriptor for one ghost footprint. Framework-free: the renderer maps role → palette. */
export interface GhostMarker {
  outcome: Exclude<GhostOutcome, 0>;
  /** glyph drawn faintly in the cell corner — kept distinct from the live status glyphs. */
  glyph: string;
  /** colour role matching the spoiler-free share legend: green / red / amber. */
  role: GhostColorRole;
  /** short, spoiler-safe label for a11y / an optional coach line — never names the answer kind. */
  label: string;
}

/** The marker for a single outcome, or null for NONE (so untouched cells render nothing). */
export function ghostMarkerFor(outcome: GhostOutcome): GhostMarker | null {
  switch (outcome) {
    case GHOST_ATE_CORRECT:
      return { outcome: GHOST_ATE_CORRECT, glyph: '✓', role: 'correct', label: 'friend ate here' };
    case GHOST_ATE_DECOY:
      return { outcome: GHOST_ATE_DECOY, glyph: '✗', role: 'decoy', label: 'friend slipped here' };
    case GHOST_THREADED:
      return { outcome: GHOST_THREADED, glyph: '◇', role: 'threaded', label: 'friend threaded here' };
    default:
      return null;
  }
}

/** The marker for a given cell id, or null when out of range / untouched. The renderer's main entry. */
export function ghostMarkerForId(outcomes: GhostOutcome[], cellId: number): GhostMarker | null {
  if (cellId < 0 || cellId >= outcomes.length) return null;
  return ghostMarkerFor(outcomes[cellId]);
}
